import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { en } from "./locales/en";
import { ru } from "./locales/ru";

export const locales = ["en", "ru"] as const;
export type Locale = (typeof locales)[number];

export const localeNames: Record<Locale, string> = { en: "English", ru: "Русский" };

// A phrase is either one string or a set of plural forms. Russian needs three
// of them where English needs two, so the shape has to allow it rather than
// pretending "1 item / N items" is universal.
export type Phrase = string | Partial<Record<Intl.LDMLPluralRule, string>>;
export type Dictionary = { [key: string]: Dictionary | Phrase };

const dictionaries: Record<Locale, Dictionary> = { en, ru };
const storageKey = "impacto.locale";

// A Russian-speaking team should not have to find a setting to be spoken to in
// Russian, so the browser's own language decides until someone says otherwise.
function detectLocale(): Locale {
  const stored = typeof localStorage === "undefined" ? null : localStorage.getItem(storageKey);
  if (stored && (locales as readonly string[]).includes(stored)) return stored as Locale;
  const preferred = typeof navigator === "undefined" ? "" : navigator.language.toLowerCase();
  return preferred.startsWith("ru") ? "ru" : "en";
}

export type Translate = (key: string, params?: Record<string, number | string>) => string;

const I18nContext = createContext<{ locale: Locale; setLocale: (locale: Locale) => void; t: Translate }>({
  locale: "en",
  setLocale: () => undefined,
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(storageKey, next);
    setLocaleState(next);
  }, []);

  // lang правится здесь, а не в setLocale: после перезагрузки язык берётся из
  // localStorage и переключатель не срабатывает, а <html lang> в разметке так и
  // оставался английским. От него зависят и переносы, и произношение в
  // скринридере, и автоперевод браузера — на перезагруженной странице он был
  // неверным ровно для тех, кто язык и поменял.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback<Translate>(
    (key, params) => translate(locale, key, params),
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT(): Translate {
  return useContext(I18nContext).t;
}

// A missing translation falls back to English and then to the key itself. It
// never throws and never renders blank: a half-translated screen is usable, a
// screen of empty boxes is not.
export function translate(locale: Locale, key: string, params?: Record<string, number | string>): string {
  const phrase = lookup(dictionaries[locale], key) ?? lookup(dictionaries.en, key);
  if (phrase === undefined) return key;
  const text = typeof phrase === "string" ? phrase : selectPlural(locale, phrase, Number(params?.count ?? 0));
  return text === undefined ? key : interpolate(text, params);
}

function lookup(dictionary: Dictionary, key: string): Phrase | undefined {
  let current: Dictionary | Phrase | undefined = dictionary;
  for (const part of key.split(".")) {
    if (typeof current !== "object" || current === null || Array.isArray(current)) return undefined;
    current = (current as Dictionary)[part];
    if (current === undefined) return undefined;
  }
  return typeof current === "object" && !isPluralForms(current) ? undefined : (current as Phrase);
}

function isPluralForms(value: Dictionary | Phrase): boolean {
  return typeof value === "object" && Object.keys(value).every((key) => pluralRuleNames.includes(key));
}

const pluralRuleNames = ["few", "many", "one", "other", "two", "zero"];

const pluralRules = new Map<Locale, Intl.PluralRules>();

function selectPlural(locale: Locale, forms: Partial<Record<Intl.LDMLPluralRule, string>>, count: number): string | undefined {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return forms[rules.select(count)] ?? forms.other ?? forms.one;
}

function interpolate(text: string, params?: Record<string, number | string>): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match));
}
